import { message } from "antd";
import axios from "axios";
import { SYSTEM_ERROR } from "config/CONSTANTS";

export function postRequest(url, param, callback) {
  return axios
    .post(url, param)
    .then(function (response) {
      const { data } = response;
      if (data.status === false) {
        message.error(response.message);
        callback(null);
      } else {
        callback(response.data);
      }
      return response.data;
    })
    .catch(function (error) {
      callback(null);
      return null;
    });
}
export function putRequest(url, param, callback) {
  return axios
    .put(url, param)
    .then(function (response) {
      const { data } = response;
      if (data.status === false) {
        message.error(response.message);
        callback(null);
      } else {
        callback(response.data);
      }
      return response.data;
    })
    .catch(function (error) {
      callback(null);
      return null;
    });
}
export function getRequest(url, callback) {
  axios
    .get(url)
    .then(function (response) {
      const { data } = response;
      if (data.status === false) {
        message.error(response.message);
        callback(null);
      } else {
        callback(response.data);
      }
    })
    .catch(function (error) {
      callback(null);
    });
}

export function deleteRequest(url, callback) {
  axios
    .delete(url)
    .then(function (response) {
      const { data } = response;
      if (data.status === false) {
        message.error(response.message);
        callback(null);
      } else {
        callback(response.data);
      }
    })
    .catch(function (error) {
      callback(null);
    });
}
export function deleteRequestWithParam(url, param, callback) {
  axios
    .delete(url, { data: param })
    .then(function (response) {
      const { data } = response;
      if (data.status === false) {
        message.error(response.message);
        callback(null);
      } else {
        callback(response.data);
      }
    })
    .catch(function (error) {
      callback(null);
    });
}

export const axiosPost = (url, param, errorMessage = "") => {
  return new Promise((resolve, reject) => {
    try {
      axios
        .post(url, param)
        .then((res) => {
          const { data } = res;
          const isSuccess = data?.result === true || data?.status === true || data?.success === true;
          if (isSuccess) {
            if (data?.data === undefined) {
              resolve(data?.msg ?? data);
            } else {
              resolve(data.data);
            }
          } else {
            console.error('[axiosPost] API returned failure:', url, 'Response:', data);
            reject(data?.msg ?? (errorMessage || "Request failed"));
          }
        })
        .catch((err) => {
          console.error('[axiosPost] Network error:', url, err);
          reject(errorMessage || err?.message || "Network error");
        });
    } catch (error) {
      reject(SYSTEM_ERROR);
    }
  });
};

// Post with application/x-www-form-urlencoded content type
export const axiosPostFormUrlencoded = (url, param, errorMessage = "") => {
  return new Promise((resolve, reject) => {
    try {
      // Convert param object to URLSearchParams for form-urlencoded format
      const params = new URLSearchParams();
      Object.keys(param).forEach((key) => {
        const value = param[key];
        // Handle arrays by joining with comma (like search_string for multiple IDs, or emails array)
        if (Array.isArray(value)) {
          // For emails array, join with comma as backend expects comma-separated string
          params.append(key, value.join(','));
        } else if (value !== null && value !== undefined) {
          params.append(key, String(value));
        }
      });
      
      // Debug: Log the actual URL-encoded string being sent
      console.log('[axiosPostFormUrlencoded] URL:', url);
      console.log('[axiosPostFormUrlencoded] Encoded params:', params.toString());
      console.log('[axiosPostFormUrlencoded] Original param object:', param);
      
      axios
        .post(url, params.toString(), {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        })
        .then((res) => {
          const { data } = res;
          const isSuccess = data?.result === true || data?.status === true || data?.success === true;
          if (isSuccess) {
            if (data?.data === undefined) {
              resolve(data?.msg ?? data);
            } else {
              resolve(data.data);
            }
          } else {
            reject(data?.msg ?? (errorMessage || "Request failed"));
          }
        })
        .catch((err) => {
          // Log full error details for debugging
          console.error('[axiosPostFormUrlencoded] Error:', err);
          console.error('[axiosPostFormUrlencoded] Error response:', err?.response?.data);
          
          // Try to extract error message from response
          let errorMsg = err?.response?.data?.msg 
            || err?.response?.data?.message;
          
          // If response is HTML (like database errors), try to parse it
          if (!errorMsg && typeof err?.response?.data === 'string' && err?.response?.data.includes('<!DOCTYPE html>')) {
            // Try to extract error from HTML
            const htmlMatch = err.response.data.match(/<p>Error Number: \d+<\/p><p>(.*?)<\/p>/);
            if (htmlMatch && htmlMatch[1]) {
              errorMsg = htmlMatch[1];
            } else {
              // Try alternative pattern
              const altMatch = err.response.data.match(/<p>(Unknown column.*?)<\/p>/);
              if (altMatch && altMatch[1]) {
                errorMsg = altMatch[1];
              }
            }
          }
          
          errorMsg = errorMsg || err?.message || errorMessage || "Network error";
          reject(errorMsg);
        });
    } catch (error) {
      reject(SYSTEM_ERROR);
    }
  });
};
export const axiosPostResult = (url, param, errorMessage = "") => {
  return new Promise((resolve, reject) => {
    try {
      axios
        .post(url, param)
        .then((res) => {
          const { data } = res;
          if (data?.result !== undefined) {
            if (data?.data === undefined && data?.result === undefined) {
              resolve(data?.msg);
            } else if (data?.result !== undefined) {
              resolve(data.result);
            } else {
              resolve(data.data);
            }
          } else if (data?.status !== undefined) {
            // Fallback for APIs that use `status`
            if (data.status === true) {
              resolve(data?.data ?? true);
            } else {
              console.error('[axiosPostResult] API returned failure:', url, 'Response:', data);
              reject(data?.msg ?? (errorMessage || "Request failed"));
            }
          } else {
            console.error('[axiosPostResult] Unexpected response format:', url, data);
            reject(data?.msg ?? (errorMessage || "Unexpected response"));
          }
        })
        .catch((err) => {
          console.error('[axiosPostResult] Network error:', url, err);
          reject(errorMessage || err?.message || "Network error");
        });
    } catch (error) {
      reject(SYSTEM_ERROR);
    }
  });
};
export const axiosPut = (url, param, errorMessage = "") => {
  return new Promise((resolve, reject) => {
    try {
      axios
        .put(url, param)
        .then((res) => {
          const { data } = res;
          if (data.status === true) {
            resolve(data.data);
          } else {
            reject(data.msg);
          }
        })
        .catch((err) => {
          reject(errorMessage, err);
        });
    } catch (error) {
      console.error(errorMessage, error);
      reject(SYSTEM_ERROR);
    }
  });
};
export const axiosGet = (url, param, errorMessage = "") => {
  return new Promise((resolve, reject) => {
    try {
      axios
        .get(url, { params: param })
        .then((res) => {
          const { data } = res;
          const isSuccess = data?.result === true || data?.status === true || data?.success === true;
          if (isSuccess) {
            resolve(data?.data ?? data);
          } else {
            console.error('[axiosGet] API returned failure:', url, 'Response:', data);
            reject(data?.msg ?? (errorMessage || "Request failed"));
          }
        })
        .catch((err) => {
          console.error('[axiosGet] Network error:', url, err);
          reject(errorMessage || err?.message || "Network error");
        });
    } catch (error) {
      console.error(errorMessage, error);
      reject(SYSTEM_ERROR);
    }
  });
};
export const axiosDelete = (url, param, errorMessage = "") => {
  return new Promise((resolve, reject) => {
    try {
      axios
        .delete(url, { data: param })
        .then((res) => {
          const { data } = res;
          if (data.status === true) {
            resolve(data.data);
          } else {
            reject(data.msg);
          }
        })
        .catch((err) => {
          reject(errorMessage, err);
        });
    } catch (error) {
      console.error(errorMessage, error);
      reject(SYSTEM_ERROR);
    }
  });
};
